import { basicToaster } from '@ui/components/Toast';
import { WalletAddress } from '@ui/components/WalletAddress';
import { isAddressInVipAllowlist } from '@ui/constants/allowlist';
import { getBaseTokenList } from '@ui/constants/tokens/tokens';
import { useOneClickTradingModalStore } from '@ui/features/OneClickTrading/store/useOneClickTradingModalStore';
import { useVipModalStore } from '@ui/features/Vip/store/useVipModalStore';
import {
  getChainId,
  useDataChainId,
} from '@ui/hooks/datachain/useDataChainStore';
import { Icon } from '@ui/lib/park/ui/icon';
import { IconButton } from '@ui/lib/park/ui/icon-button';
import { Menu } from '@ui/lib/park/ui/menu';
import { Text } from '@ui/lib/park/ui/text';
import { getExplorerLink } from '@ui/utils/links';
import {
  ArrowLeftRight,
  ChevronRight,
  Copy,
  ExternalLink,
  Lock,
  LogOut,
  Star,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { useCopyToClipboard } from 'react-use';
import { Flex, HStack } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { useDisconnectWallet } from '../hooks/useDisconnectWallet';
import { BalanceSection } from './BalanceSection';

const DISPLAY_TOKENS = ['MON', 'USDC', 'LVUSD', 'LVMON', 'LV'];

const menuItemStyle = {
  px: '14px',
  py: '10px',
  mx: '0',
  borderRadius: '0px',
  color: 'v2.text.secondary',
  _hover: {
    bg: 'v2.bg.surface1',
    color: 'v2.text.primary',
  },
  _highlighted: {
    bg: 'v2.bg.surface1',
    color: 'v2.text.primary',
  },
} as const;

const MenuItemRow = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
}) => (
  <HStack justifyContent="space-between" alignItems="center" w="full">
    <HStack alignItems="center" gap="10px">
      <Icon boxSize="16px" color="v2.text.muted" flexShrink={0}>
        {icon}
      </Icon>
      <Text textStyle="v2.label" color="inherit">
        {label}
      </Text>
    </HStack>
    <Icon boxSize="14px" color="v2.text.subtle" flexShrink={0}>
      <ChevronRight strokeWidth={1.5} />
    </Icon>
  </HStack>
);

export const UserWalletMenu = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const { address, connector } = useAccount();
  const chainId = useDataChainId();
  const disconnectWallet = useDisconnectWallet();
  const [, copy] = useCopyToClipboard();
  const openOneClickTrading = useOneClickTradingModalStore((s) => s.open);
  const openVipModal = useVipModalStore((s) => s.open);
  const showVipEntry = isAddressInVipAllowlist(address);

  return (
    <Menu.Root
      lazyMount
      unmountOnExit
      positioning={{
        placement: 'bottom-end',
      }}
    >
      <Menu.Trigger asChild flexShrink={0}>
        {children}
      </Menu.Trigger>
      <Menu.Positioner>
        <Menu.Content
          w="300px"
          bg="v2.bg.panel"
          border="1px solid"
          borderColor="v2.border.default"
          borderRadius="4px"
          boxShadow="modal"
          py={0}
          overflow="hidden"
        >
          <Menu.ItemGroup maxH="450px" overflow="auto" gap="0" py="0" px="0">
            {address ? (
              <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="8px"
                px="14px"
                py="10px"
                borderBottom="1px solid"
                borderColor="v2.border.hairline"
                cursor="default"
              >
                <WalletAddress
                  textStyle="numbers.small"
                  color="v2.text.primary"
                  address={address}
                />
                <HStack gap="2px">
                  <IconButton
                    h="26px"
                    w="26px"
                    minW="26px"
                    borderRadius="2px"
                    aria-label="Copy address"
                    outline="none"
                    variant="ghost"
                    color="v2.text.subtle"
                    _hover={{
                      color: 'v2.text.primary',
                      background: 'v2.bg.surface1',
                    }}
                    onClick={() => {
                      copy(address);
                      basicToaster.success({
                        title: 'Copied to clipboard',
                      });
                    }}
                  >
                    <Icon boxSize="14px">
                      <Copy strokeWidth={1.5} />
                    </Icon>
                  </IconButton>
                  <IconButton
                    h="26px"
                    w="26px"
                    minW="26px"
                    borderRadius="2px"
                    aria-label="Open in explorer"
                    outline="none"
                    variant="ghost"
                    color="v2.text.subtle"
                    _hover={{
                      color: 'v2.text.primary',
                      background: 'v2.bg.surface1',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      if (address) {
                        const href = getExplorerLink(
                          address,
                          'address',
                          getChainId(),
                        );
                        window.open(href, '_blank');
                      }
                    }}
                  >
                    <Icon boxSize="14px">
                      <ExternalLink strokeWidth={1.5} />
                    </Icon>
                  </IconButton>
                  <IconButton
                    h="26px"
                    w="26px"
                    minW="26px"
                    borderRadius="2px"
                    aria-label="Disconnect"
                    outline="none"
                    variant="ghost"
                    color="v2.short.default"
                    _hover={{
                      color: 'v2.short.default',
                      background: 'v2.bg.surface1',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      disconnectWallet();
                    }}
                  >
                    <Icon boxSize="14px">
                      <LogOut strokeWidth={1.5} />
                    </Icon>
                  </IconButton>
                </HStack>
              </Flex>
            ) : null}
            <Menu.Item
              value="bridge"
              {...menuItemStyle}
              onClick={() => {
                window.open(
                  'https://monadbridge.com/?fromChain=Arbitrum&toChain=Monad&toToken=USDC&fromToken=USDC',
                  '_blank',
                );
              }}
            >
              <MenuItemRow
                icon={<ArrowLeftRight strokeWidth={1.5} />}
                label={t('common.Bridge Funds')}
              />
            </Menu.Item>
            <Menu.Item
              value="one-click-trading"
              {...menuItemStyle}
              onClick={() => {
                openOneClickTrading();
              }}
            >
              <MenuItemRow
                icon={<Zap strokeWidth={1.5} />}
                label={t('common.One-Click Trading', 'One-Click Trading')}
              />
            </Menu.Item>

            {showVipEntry && (
              <Menu.Item
                value="contact-vip-manager"
                {...menuItemStyle}
                onClick={() => {
                  openVipModal();
                }}
              >
                <MenuItemRow
                  icon={<Star strokeWidth={1.5} />}
                  label={t('vip.contactManager', 'Contact VIP Manager')}
                />
              </Menu.Item>
            )}

            {connector?.id === 'io.google.privy.wallet' && (
              <Menu.Item
                value="export-wallet"
                {...menuItemStyle}
                onClick={() => {
                  window.open('/export-wallet', '_blank');
                }}
              >
                <MenuItemRow
                  icon={<Lock strokeWidth={1.5} />}
                  label={t('common.Export Wallet', 'Export Wallet')}
                />
              </Menu.Item>
            )}

            <BalanceSection
              tokens={useMemo(() => {
                const tokens = getBaseTokenList(chainId);

                return DISPLAY_TOKENS.map((symbol) =>
                  tokens.find((t) => t.symbol === symbol),
                ).filter((t) => !!t) as any;
              }, [chainId])}
            />
          </Menu.ItemGroup>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
};
